import GeoJsonFetchLayer from './GeoJsonFetchLayer';
import { registerCursorHandlers } from '../utils/maplibre-helpers';

// Building lines of local plans (BuildingLines.php), one style per kind, for this layer and its legend. The parcel
// page draws them the same way (assets/public/js/szczegoly.js BUILDING_LINE_STYLES, kept equal by
// common/tests/js/building-line-styles.test.mjs): a limit dashed and a mandatory line solid, both red, a recommended
// line amber and one of an unknown kind slate, both dotted. Each colour reaches 3:1 on the light and on the dark card,
// where the legend of the parcel page sits; the basemap stays light.
export const BUILDING_LINE_STYLES = [
    { kind: 'building_line_limit', label: 'Nieprzekraczalna linia zabudowy', color: '#dc2626', dash: [3, 2] },
    { kind: 'building_line_mandatory', label: 'Obowiązująca linia zabudowy', color: '#dc2626', dash: null },
    { kind: 'building_line_recommended', label: 'Zalecana linia zabudowy', color: '#b45309', dash: [1, 2] },
    { kind: 'building_line', label: 'Linia zabudowy bez rodzaju w serwisie gminy', color: '#64748b', dash: [1, 2] },
];

/** The legend swatch of a building line style: long enough to show its dashes. */
export function buildingLineLegendShape(style) {
    if (!style.dash) return 'long-line';
    return style.dash[0] > 1 ? 'long-dashed-line' : 'long-dotted-line';
}

// Groups of local plan designations: one colour per group on this layer, in its legend and on the point panel card
// (layer-point-card.js). A zone's `symbol` matches a group when it equals one of its symbols, upper case; any other
// symbol takes the fallback colour. G and K are no groups: in plan symbols they mark gas and sewage networks.
export const MPZP_DESIGNATION_GROUPS = [
    { label: 'Mieszkalnictwo', rgb: [20, 184, 166], symbols: ['MN', 'MN-U', 'MNU', 'MW'] },
    { label: 'Usługi', rgb: [37, 99, 235], symbols: ['U', 'U-MN', 'U/P', 'UP'] },
    { label: 'Produkcja', rgb: [234, 88, 12], symbols: ['P', 'PU', 'AG'] },
    { label: 'Zieleń i rolnictwo', rgb: [22, 163, 74], symbols: ['ZP', 'ZN', 'ZL', 'R'] },
    { label: 'Komunikacja', rgb: [71, 85, 105], symbols: ['KD', 'KDD', 'KDL', 'KDG', 'KDW'] },
    { label: 'Wody', rgb: [14, 165, 233], symbols: ['WS', 'W'] },
];
const MPZP_DESIGNATION_FALLBACK_RGB = [217, 119, 6];

export function rgbHex(rgb) {
    return '#' + rgb.map((channel) => channel.toString(16).padStart(2, '0')).join('');
}

/** The designation group of a zone symbol, or null for a symbol drawn in the fallback colour. */
export function mpzpDesignationGroup(symbol) {
    const normalized = String(symbol == null ? '' : symbol).toUpperCase();
    return MPZP_DESIGNATION_GROUPS.find((group) => group.symbols.includes(normalized)) || null;
}

export default class MpzpImportedLayer extends GeoJsonFetchLayer {
    static ID = 'mpzpRu';
    static DESCRIPTION = 'Miejscowe plany zagospodarowania przestrzennego z Rejestru urbanistycznego — przeznaczenia z importu';
    static MIN_ZOOM = 13;
    static SOURCE_ID = 'src-mpzp-ru';
    static FILL_LAYER_ID = 'lyr-mpzp-ru-fill';
    static OUTLINE_LAYER_ID = 'lyr-mpzp-ru-line';
    static LABEL_LAYER_ID = 'lyr-mpzp-ru-label';
    // Building lines of local plans come with the zones from zoom 15 (RejestrUrbanistycznyMpzpGeoJsonService), one
    // layer per kind (BUILDING_LINE_STYLES).
    static BUILDING_LINE_MIN_ZOOM = 15;
    static BUILDING_LINE_LAYERS = BUILDING_LINE_STYLES.map((style) => ({
        id: 'lyr-mpzp-ru-' + style.kind.replace(/_/g, '-'),
        kinds: [style.kind],
        color: style.color,
        dash: style.dash,
    }));
    static BASE_LAYER_IDS = [MpzpImportedLayer.FILL_LAYER_ID, MpzpImportedLayer.OUTLINE_LAYER_ID]
        .concat(MpzpImportedLayer.BUILDING_LINE_LAYERS.map((layer) => layer.id), [MpzpImportedLayer.LABEL_LAYER_ID]);
    static RENDER_ORDER_LAYER_IDS = MpzpImportedLayer.BASE_LAYER_IDS;
    static NOT_A_BUILDING_LINE = ['!=', ['get', 'featureKind'], 'building_line'];

    constructor(config = {}) {
        super(config);
        this.interactionsBound = false;
    }

    getSourceId() {
        return MpzpImportedLayer.SOURCE_ID;
    }

    getLayerIds() {
        return MpzpImportedLayer.BASE_LAYER_IDS;
    }

    getRenderOrderLayerIds() {
        return MpzpImportedLayer.RENDER_ORDER_LAYER_IDS;
    }

    getFeatureKey(feature) {
        const props = feature && feature.properties ? feature.properties : {};
        return props.id || props.gmlId || this.featureGeometryKey(feature);
    }

    buildUrl() {
        return this.config.baseUrl + 'api/urban-planning-mpzp-geojson?bbox='
            + encodeURIComponent(this.config.getCurrentBbox(0.12))
            + '&zoom=' + encodeURIComponent(this.config.getCurrentZoom())
            + '&limit=900';
    }

    symbolColorExpression(alpha) {
        const rgba = (rgb) => 'rgba(' + rgb.join(', ') + ', ' + alpha + ')';
        return [
            'case',
            ['has', 'symbol'],
            [
                'match',
                ['upcase', ['to-string', ['get', 'symbol']]],
                ...MPZP_DESIGNATION_GROUPS.flatMap((group) => [group.symbols, rgba(group.rgb)]),
                rgba(MPZP_DESIGNATION_FALLBACK_RGB)
            ],
            rgba(MPZP_DESIGNATION_FALLBACK_RGB)
        ];
    }

    ensureInteractionHandlers() {
        const map = this.getMap();
        if (!map || this.interactionsBound) {
            return;
        }

        MpzpImportedLayer.BASE_LAYER_IDS.forEach((layerId) => {
            if (map.getLayer(layerId)) {
                registerCursorHandlers(map, layerId);
            }
        });

        this.interactionsBound = true;
    }

    _addBaseLayers(map, sourceId) {
        map.addLayer({
            id: MpzpImportedLayer.FILL_LAYER_ID,
            type: 'fill',
            source: sourceId,
            filter: MpzpImportedLayer.NOT_A_BUILDING_LINE,
            paint: {
                'fill-color': this.symbolColorExpression(0.24),
                'fill-opacity': 1
            }
        });
        map.addLayer({
            id: MpzpImportedLayer.OUTLINE_LAYER_ID,
            type: 'line',
            source: sourceId,
            filter: MpzpImportedLayer.NOT_A_BUILDING_LINE,
            paint: {
                'line-color': this.symbolColorExpression(0.78),
                'line-width': [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    13, 1,
                    17, 2.5
                ]
            }
        });
        MpzpImportedLayer.BUILDING_LINE_LAYERS.forEach((layer) => {
            const paint = {
                'line-color': layer.color,
                'line-width': ['interpolate', ['linear'], ['zoom'], 15, 1.6, 18, 3],
                'line-opacity': 0.95,
            };
            if (layer.dash) paint['line-dasharray'] = layer.dash;
            map.addLayer({
                id: layer.id,
                type: 'line',
                source: sourceId,
                minzoom: MpzpImportedLayer.BUILDING_LINE_MIN_ZOOM,
                filter: ['match', ['get', 'lineKind'], layer.kinds, true, false],
                paint,
            });
        });
        map.addLayer({
            id: MpzpImportedLayer.LABEL_LAYER_ID,
            type: 'symbol',
            source: sourceId,
            minzoom: 15,
            filter: MpzpImportedLayer.NOT_A_BUILDING_LINE,
            layout: {
                'text-field': ['coalesce', ['get', 'label'], ['get', 'symbol']],
                'text-size': [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    15, 10,
                    18, 13
                ],
                'text-allow-overlap': false,
                // The service sends the bare outlines of plans first, so that zones are drawn over them;
                // their long titles must not take the place of the designations of the zones.
                'symbol-sort-key': ['case', ['any', ['to-boolean', ['get', 'designation']], ['to-boolean', ['get', 'symbol']]], 0, 1]
            },
            paint: {
                'text-color': '#0f172a',
                'text-halo-color': 'rgba(255, 255, 255, 0.9)',
                'text-halo-width': 1.5
            }
        });
        this.ensureInteractionHandlers();
    }

    renderData(data) {
        return this.renderSourceData(data, this._addBaseLayers.bind(this));
    }
}
